import React, { useState } from 'react';
import { api } from '../api.js';
import { Card, ErrorBanner, Loading, Money, ProofReference, StateChip, useAsync, useSession, useToast } from '../ui.jsx';
import { dateLabel, money, nameOf, unitName } from '../lib/format.js';
import { ReasonModal } from './item-detail.jsx';
import { Link } from '../main.jsx';

/**
 * The 確認人 queue. Being an 管理員 does not grant this action: only the member
 * designated on the 付款項目 may confirm or return a 付款提交.
 */
export default function ConfirmationsPage() {
  const session = useSession();
  const notify = useToast();
  const queue = useAsync(() => api.pendingSubmissions(), []);
  const [dialog, setDialog] = useState(null);
  const [error, setError] = useState(null);

  if (queue.loading) return <Loading />;
  if (queue.error) return <ErrorBanner error={queue.error} />;

  const act = async (action, message) => {
    setError(null);
    try {
      await action();
      notify(message);
      setDialog(null);
      queue.reload();
    } catch (failure) {
      setError(failure);
    }
  };

  const rows = queue.data.items.flatMap((item) => item.dues.flatMap((due) => due.submissions
    .filter((submission) => submission.state === 'pending')
    .map((submission) => ({ item, due, submission }))));

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <div className="eyebrow">確認人工作</div>
          <h1>待我確認的付款提交</h1>
          <p className="small muted">確認後金額才會扣減仍欠款；退回必須填寫原因，原提交與圖片仍會保留。</p>
        </div>
      </div>
      <ErrorBanner error={error} />
      <Card testId="confirmation-queue">
        {rows.length === 0 ? (
          <p className="small muted" data-testid="confirmation-empty">目前沒有待你確認的付款提交。</p>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>付款項目</th>
                  <th>付款成員</th>
                  <th>證明參考</th>
                  <th className="num">金額</th>
                  <th>提交日期</th>
                  <th>提示</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map(({ item, due, submission }) => {
                  const wouldCreateBalance = Math.max(
                    0,
                    due.confirmed_cents + due.credited_cents + submission.amount_cents - due.amount_cents,
                  );
                  return (
                    <tr key={submission.id} data-testid={`queue-${submission.reference}`}>
                      <td>
                        <Link to={`/items/${item.id}`}>{item.title}</Link>
                        <div className="small muted">{unitName(session.all_units, item.unit_id)} · 到期 {dateLabel(item.due_date)}</div>
                      </td>
                      <td>{nameOf(session.members, due.member_id)}</td>
                      <td><ProofReference reference={submission.reference} /></td>
                      <td className="num"><Money cents={submission.amount_cents} /></td>
                      <td>{dateLabel(submission.submitted_on)}</td>
                      <td className="small">
                        {wouldCreateBalance > 0 ? (
                          <span className="chip pending" data-testid={`balance-warning-${submission.reference}`}>
                            確認會產生結餘 {money(wouldCreateBalance)}
                          </span>
                        ) : <StateChip state={due.state} label={due.state_label} overdue={due.overdue} />}
                      </td>
                      <td>
                        <div className="row">
                          <button
                            type="button"
                            className="primary small"
                            data-testid={`queue-confirm-${submission.reference}`}
                            onClick={() => act(() => api.confirmSubmission(submission.id), '付款提交已確認')}
                          >
                            確認
                          </button>
                          <button
                            type="button"
                            className="small"
                            data-testid={`queue-return-${submission.reference}`}
                            onClick={() => setDialog(submission)}
                          >
                            退回
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      {dialog ? (
        <ReasonModal
          title="退回付款提交"
          subtitle={`${dialog.reference} · ${money(dialog.amount_cents)}`}
          note="退回不代表付款證明被刪除；成員重新提交會建立新紀錄。"
          onClose={() => setDialog(null)}
          onSubmit={(reason) => act(() => api.returnSubmission(dialog.id, { reason }), '付款提交已退回')}
        />
      ) : null}
    </div>
  );
}
